import { ConfirmDialog } from '@components/ConfirmDialog/ConfirmDialog';
import { LoadingActionButton } from '@components/LoadingActionButton/LoadingActionButton';
import { useNow } from '@lib/hooks/useNow';
import { ApiError } from '@lib/network/api';
import {
  describePostDetail,
  describeScheduleError,
  type SocialPost,
} from '@lib/social/scheduling/posts';
import { canCancelStatus } from '@lib/social/scheduling/status';
import { showToast } from '@lib/utils/toastmaster';
import { AddRounded, ArrowBackRounded, OpenInNewRounded } from '@mui/icons-material';
import { IconButton, Skeleton, Tooltip } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { AppPage } from '@pages/AppPage';
import { scheduleSettings } from '@stores/ScheduleSettings';
import { socialScheduleStore } from '@stores/SocialSchedule';
import { trackingStore } from '@stores/Tracking';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { StatusPill } from './components/StatusPill/StatusPill';
import { notifyStatusConflict } from './postConflict';
import { PostForm, savePost } from './PostForm/PostForm';
import { isStatusConflict, PostFormProvider, usePostForm } from './PostForm/PostFormStore';
import css from './PostPages.module.scss';
import scheduleCss from './SchedulePage.module.scss';
import { composePath, schedulePath } from './schedulePath';

type PostAction = 'cancel' | 'retry';

/**
 * Run cancel or retry and put every failure in front of the user: a status
 * conflict reloads the post, anything else is a toast. Retry with unsaved
 * edits saves them first ("edit, then retry" is the contract's repair path),
 * so a corrected caption is never left behind.
 */
const usePostAction = (post: SocialPost, now: Date) => {
  const form = usePostForm();
  const navigate = useNavigate();
  return async (action: PostAction): Promise<void> => {
    try {
      if (action === 'retry' && form.pendingEdit && !(await savePost(form))) {
        // The save failed and said so; the retry waits for a fixed save.
        return;
      }
      const updated =
        action === 'cancel'
          ? await socialScheduleStore.cancel(post.id)
          : await socialScheduleStore.retry(post.id);
      trackingStore.schedulePostAction({ action, status: post.status });
      showToast(action === 'cancel' ? 'social-post-cancelled' : 'social-post-retried');
      navigate(schedulePath(updated.publishAt, now));
    } catch (error) {
      if (isStatusConflict(error)) {
        await notifyStatusConflict(post.id);
        return;
      }
      showToast('social-post-action-failed', {
        action,
        description: describeScheduleError(error),
        error,
      });
    }
  };
};

/** Cancel, gated by the status. Always confirms: a cancel recalls nothing the platform may already have. */
const CancelPostButton = observer(function CancelPostButton({
  post,
  now,
}: {
  post: SocialPost;
  now: Date;
}) {
  const run = usePostAction(post, now);
  const [confirming, setConfirming] = useState(false);
  if (!canCancelStatus(post.status)) return null;
  const unconfirmed = post.attentionReason === 'attempt_unconfirmed';
  return (
    <>
      <ActionButton variant="secondary" onClick={() => setConfirming(true)}>
        Cancel post
      </ActionButton>
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={async () => {
          await run('cancel');
          setConfirming(false);
        }}
        title="Cancel this post?"
        confirmLabel="Cancel post"
        loadingText="Cancelling…"
      >
        It will not be published, and it stays in your schedule as cancelled.
        {unconfirmed && (
          <> The platform may already have this post; cancelling does not take it down there.</>
        )}
      </ConfirmDialog>
    </>
  );
});

/**
 * Retry, gated by the status and the attention reason; the row's primary.
 * An unconfirmed attempt confirms first: a retry can post twice.
 */
const RetryPostButton = observer(function RetryPostButton({
  post,
  now,
}: {
  post: SocialPost;
  now: Date;
}) {
  const form = usePostForm();
  const run = usePostAction(post, now);
  const [confirming, setConfirming] = useState(false);
  if (!form.retryable) return null;
  const unconfirmed = post.attentionReason === 'attempt_unconfirmed';
  const needsReauth = post.attentionReason === 'connection_needs_reauth';
  const saveFirst = form.pendingEdit !== null;
  return (
    <>
      <Tooltip
        title={
          needsReauth
            ? 'Reconnect the account first.'
            : saveFirst && !form.canSubmit
              ? 'Finish the edits before retrying.'
              : ''
        }
      >
        <span>
          <LoadingActionButton
            variant="primary"
            disabled={needsReauth || (saveFirst && !form.canSubmit)}
            loadingText={saveFirst ? 'Saving…' : 'Retrying…'}
            onClick={() => (unconfirmed ? setConfirming(true) : run('retry'))}
          >
            {saveFirst ? 'Save and retry' : 'Retry'}
          </LoadingActionButton>
        </span>
      </Tooltip>
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={async () => {
          await run('retry');
          setConfirming(false);
        }}
        title="Retry this post?"
        confirmLabel={saveFirst ? 'Save and retry' : 'Retry'}
        confirmVariant="primary"
        loadingText={saveFirst ? 'Saving…' : 'Retrying…'}
      >
        The platform may already have this post. Check the account first: a retry can post it twice.
      </ConfirmDialog>
    </>
  );
});

/**
 * The loaded post: header with the way back and, for a post with a render,
 * the way to a new post of it; then the form with the status line above it
 * and cancel and retry in its action row. "Schedule again" sits in the strip
 * because the action row only renders while the post is editable, and a
 * published post is the one to post again.
 */
const PostPageBody = observer(function PostPageBody({
  post,
  now,
}: {
  post: SocialPost;
  now: Date;
}) {
  const navigate = useNavigate();
  const detail = describePostDetail(post, now.getTime());
  const { renderId } = post;
  return (
    <>
      <div className={css.headerStrip}>
        <IconButton
          className={css.backButton}
          aria-label="Back to the schedule"
          onClick={() => navigate(schedulePath(post.publishAt, now))}
        >
          <ArrowBackRounded />
        </IconButton>
        <h2 className={css.title}>Scheduled post</h2>
        {renderId !== null && (
          <span className={css.headerAction}>
            <ActionButton variant="secondary" onClick={() => navigate(composePath(renderId))}>
              <AddRounded />
              &nbsp;Schedule again
            </ActionButton>
          </span>
        )}
      </div>
      <div className={css.scrollableContent}>
        <PostForm
          now={now}
          intro={
            <div className={css.statusBlock}>
              <StatusPill status={post.status} />
              {detail && <span>{detail}</span>}
              {post.permalink && (
                <a
                  className={css.viewLink}
                  href={post.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View post
                  <OpenInNewRounded />
                </a>
              )}
            </div>
          }
          leadingActions={<CancelPostButton post={post} now={now} />}
          trailingActions={<RetryPostButton post={post} now={now} />}
        />
      </div>
    </>
  );
});

/** `/schedule/post/:postId`: the post's page, with edit, cancel and retry as its status allows. */
export const PostPage = observer(function PostPage() {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  // Ticking: the status line's elapsed time and "this time has passed" describe a moving quantity.
  const now = scheduleSettings.inZone(useNow());

  // Read again on every open: a held copy may be a minute stale, and a pasted link has none.
  useEffect(() => {
    if (postId) void socialScheduleStore.loadPost(postId);
  }, [postId]);

  // A publishing post moves while the page is open, as it does on the schedule list.
  useEffect(() => {
    socialScheduleStore.startRefreshing();
    return () => socialScheduleStore.stopRefreshing();
  }, []);

  const post = postId ? socialScheduleStore.post(postId) : undefined;
  const load = postId ? socialScheduleStore.postLoadState(postId) : undefined;

  // The minute refresh reconciles windows with drop semantics: a post moved
  // from another tab leaves the cache mid-visit. "Had it, now gone, no
  // error" is a cue to read it again by id, not a loading state.
  useEffect(() => {
    if (postId && !post && load && !load.fetching && !load.error) {
      void socialScheduleStore.loadPost(postId);
    }
  }, [postId, post, load]);

  if (!postId) return <Navigate to="/schedule" replace />;

  return (
    <AppPage header="Scheduled post">
      <div className={css.container}>
        {post ? (
          <PostFormProvider arguments={['edit', postId]}>
            <PostPageBody post={post} now={now} />
          </PostFormProvider>
        ) : (
          <>
            <div className={css.headerStrip}>
              <IconButton
                className={css.backButton}
                aria-label="Back to the schedule"
                onClick={() => navigate(schedulePath(null, now))}
              >
                <ArrowBackRounded />
              </IconButton>
              <h2 className={css.title}>Scheduled post</h2>
            </div>
            <div className={css.scrollableContent}>
              {load?.error ? (
                <div className={scheduleCss.stateRow}>
                  {load.error instanceof ApiError && load.error.status === 404 ? (
                    <span>This post could not be found. It may belong to another account.</span>
                  ) : (
                    <>
                      <span>Something went wrong. Try again later.</span>
                      <ActionButton
                        variant="secondary"
                        onClick={() => void socialScheduleStore.loadPost(postId)}
                      >
                        Retry
                      </ActionButton>
                    </>
                  )}
                </div>
              ) : (
                <Skeleton
                  variant="rounded"
                  height={320}
                  sx={{
                    borderRadius: '14px',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    maxWidth: 720,
                    mx: 'auto',
                  }}
                />
              )}
            </div>
          </>
        )}
      </div>
    </AppPage>
  );
});
